import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/apiConfig';
import { formatDateTime } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import SectionCard from './SectionCard';

export interface SyncActivityLine {
  id: number;
  at: string;
  level: string;
  context: string;
  message: string;
  text: string;
}

export interface SyncActivityLiveLogContentProps {
  embedded?: boolean;
  maxLines?: number;
  showFooterNote?: boolean;
}

function levelColor(level: string, colors: ReturnType<typeof useTheme>['colors']): string {
  switch (level) {
    case 'error':
      return 'text-rose-400';
    case 'warn':
    case 'warning':
      return 'text-amber-400';
    case 'debug':
      return 'text-slate-400';
    default:
      return colors.text;
  }
}

export const SyncActivityLiveLogContent: React.FC<SyncActivityLiveLogContentProps> = ({
  embedded = false,
  maxLines = 300,
  showFooterNote = true,
}) => {
  const { colors } = useTheme();
  const [lines, setLines] = useState<SyncActivityLine[]>([]);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  const appendLines = useCallback(
    (incoming: SyncActivityLine[]) => {
      if (incoming.length === 0) return;
      setLines((prev) => {
        const ids = new Set(prev.map((l) => l.id));
        const merged = [...prev];
        for (const line of incoming) {
          if (!ids.has(line.id)) merged.push(line);
        }
        return merged.slice(-maxLines);
      });
    },
    [maxLines],
  );

  useEffect(() => {
    if (paused) {
      setConnected(false);
      return;
    }

    const controller = new AbortController();
    let closed = false;

    const connect = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/system-health/sync-activity/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: controller.signal,
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error(`Stream failed (${res.status})`);
        }

        setConnected(true);
        setError(null);

        const reader = res.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');
          buffer = chunks.pop() ?? '';

          for (const chunk of chunks) {
            const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const payload = JSON.parse(dataLine.slice(6)) as { type: string; line?: SyncActivityLine };
              if (payload.type === 'line' && payload.line) {
                appendLines([payload.line]);
              }
            } catch {
              // ignore malformed SSE chunk
            }
          }
        }
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        const msg = err instanceof Error ? err.message : 'Stream disconnected';
        setError(msg);
        setConnected(false);
      }
    };

    void connect();

    return () => {
      closed = true;
      controller.abort();
      setConnected(false);
    };
  }, [paused, appendLines]);

  useEffect(() => {
    if (!autoScrollRef.current || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines]);

  const handleClear = () => setLines([]);

  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <span
        className={cn(
          'inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full',
          connected ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : colors.surfaceHover,
          !connected && colors.textMuted,
        )}
      >
        <span className={cn('w-2 h-2 rounded-full', connected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400')} />
        {paused ? 'Paused' : connected ? 'Live' : error ? 'Disconnected' : 'Connecting…'}
      </span>
      <button
        type="button"
        onClick={() => setPaused((p) => !p)}
        className={cn('text-xs font-medium px-2.5 py-1 rounded-lg border', colors.border, colors.text, colors.interactiveHover)}
      >
        {paused ? 'Resume' : 'Pause'}
      </button>
      <button
        type="button"
        onClick={handleClear}
        className={cn('text-xs font-medium px-2.5 py-1 rounded-lg border', colors.border, colors.textMuted, colors.interactiveHover)}
      >
        Clear
      </button>
    </div>
  );

  return (
    <div className={cn(embedded && 'flex flex-col min-h-0 flex-1')}>
      <div className={cn('mb-2 flex-shrink-0', embedded ? '' : 'flex justify-end')}>{controls}</div>

      {error && !paused && (
        <p className={cn('text-xs mb-2 flex-shrink-0', colors.error)}>
          {error}. Reconnecting requires refresh or toggle Pause.
        </p>
      )}

      <div
        ref={scrollRef}
        className={cn(
          'rounded-lg border font-mono text-xs leading-relaxed overflow-auto p-3',
          'bg-slate-950 text-slate-100 border-slate-800',
          embedded ? 'flex-1 min-h-0' : 'max-h-[320px] min-h-[160px]',
        )}
        onScroll={(e) => {
          const el = e.currentTarget;
          autoScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
        }}
      >
        {lines.length === 0 ? (
          <p className="text-slate-500">
            Waiting for sync log lines… Trigger sync or wait for the cron (every minute).
          </p>
        ) : (
          lines.map((line) => (
            <div key={line.id} className="whitespace-pre-wrap break-all py-0.5 border-b border-slate-800/80 last:border-0">
              <span className="text-slate-500 select-none">{formatDateTime(line.at)} </span>
              <span className={cn('uppercase text-[10px] font-bold mr-2', levelColor(line.level, colors))}>
                {line.level}
              </span>
              <span className="text-sky-400/90">[{line.context}]</span>{' '}
              <span>{line.message}</span>
            </div>
          ))
        )}
      </div>

      {showFooterNote && (
        <p className={cn('text-xs mt-2 flex-shrink-0', colors.textMuted)}>
          Shows NestJS logger output from this backend process (same as{' '}
          <code className="font-mono">docker logs ucomm_backend</code>). Lines appear after deploy; history is kept in
          memory only (last ~{maxLines} lines).
        </p>
      )}
    </div>
  );
};

const SyncActivityLiveLog: React.FC = () => (
  <SectionCard
    zone="mail"
    title="Live sync log"
    subtitle="Backend stdout filtered like docker logs — starting sync, Opened box, batch, completed, stale, timeout"
  >
    <SyncActivityLiveLogContent showFooterNote maxLines={300} />
  </SectionCard>
);

export default SyncActivityLiveLog;
